"use strict";
/*
 * Copyright 2023, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkChoicesConditional = exports.checkChoiceConditional = exports.checkConditional = void 0;
const types_1 = require("evolution-common/lib/services/questionnaire/types");
const surveyHelper = __importStar(require("evolution-common/lib/utils/helpers"));
const LodashExtensions_1 = require("chaire-lib-common/lib/utils/LodashExtensions");
/**
 * Validate a conditional value and return the result
 * @param conditional The conditional value of function
 * @param interview The interview
 * @param path The path of the value to conditionally test
 * @param user The current user
 * @returns An array, where the first value is whether the condition result. The
 * second and third elements are the values to set to this field (2nd: value and 3rd: customValue for customChoice) if the
 * condition fails.
 */
const checkConditional = (conditional, interview, path, user) => {
    if (conditional === undefined) {
        return [true, undefined, undefined];
    }
    if (typeof conditional === 'boolean') {
        return [conditional, undefined, undefined];
    }
    if (Array.isArray(conditional)) {
        return [conditional[0], conditional[1], conditional[2]];
    }
    else if (typeof conditional === 'function') {
        try {
            const conditionalResult = conditional(interview, path, user);
            if (typeof conditionalResult === 'boolean') {
                return [conditionalResult, undefined, undefined];
            }
            else if (Array.isArray(conditionalResult)) {
                // there is assigned values
                return [
                    conditionalResult[0],
                    conditionalResult[1] !== undefined ? conditionalResult[1] : undefined,
                    conditionalResult[2] !== undefined
                        ? conditionalResult[2]
                        : conditionalResult[1] !== undefined
                            ? null
                            : undefined
                ];
            }
            else {
                surveyHelper.devLog(`Widget returned a non boolean condition: ${path}`);
                return [Boolean(conditionalResult), undefined, undefined];
            }
        }
        catch (error) {
            // If there is an error during conditional check, just ignore the error and hide the field to reduce probability of side-effects.
            // TODO: define a better way to deal with errors.
            console.warn(`conditional error for ${path}:`, error);
            return [false, undefined, undefined];
        }
    }
    else {
        return [true, undefined, undefined];
    }
};
exports.checkConditional = checkConditional;
/**
 * Validate a choice conditional value and return the result
 * @param conditional The choice conditional value of function
 * @param interview The interview
 * @param path The path of the value to conditionally test
 * @param user The current user
 * @returns An array, where the first value is whether the condition result. The
 * second are the values to set to this field if the
 * condition fails.
 */
const checkChoiceConditional = (conditional, interview, path, user) => {
    if (conditional === undefined) {
        return [true, undefined];
    }
    if (typeof conditional === 'boolean') {
        return [conditional, undefined];
    }
    if (Array.isArray(conditional)) {
        return [conditional[0], conditional[1] !== undefined ? conditional[1] : undefined];
    }
    else {
        try {
            const conditionalResult = conditional(interview, path, user);
            if (typeof conditionalResult === 'boolean') {
                return [conditionalResult, undefined];
            }
            else if (conditionalResult.length && conditionalResult.length > 1) {
                // there is assigned values
                return [conditionalResult[0], conditionalResult[1]];
            }
            else {
                return [conditionalResult[0], undefined];
            }
        }
        catch (error) {
            // If there is an error during conditional check, just ignore the error and hide the field to reduce probability of side-effects.
            // TODO: find a better way to deal with errors.
            console.warn(`choice conditional error for path ${path}: `, error);
            return [false, undefined];
        }
    }
};
exports.checkChoiceConditional = checkChoiceConditional;
/**
 * Check whether all selected choices are still visible, or if we need to change
 * the value of the response
 * @param choices the widget choices or grouped choices
 * @param interview The interview
 * @param path The path of the value to conditionally test
 * @param user The current user
 * @returns An array, where the first value is whether all selected choices are
 * still visible. The second element is the value to set to this field if any of
 * the conditional are now `false`.
 */
const checkChoicesConditional = (value, choices, interview, path, user) => {
    if ((0, LodashExtensions_1._isBlank)(value)) {
        return [true, undefined];
    }
    const valueAsArray = Array.isArray(value) ? value : [value]; // value can be multiple choices (array)
    // Get the value along with its corresponding choice
    const parsedChoices = (typeof choices === 'function' ? choices(interview, path, user) : choices).flatMap((choice) => {
        return (0, types_1.isGroupedChoice)(choice) ? choice.choices : [choice];
    });
    // Array of [value, [conditional Response, new value]]
    const valueAndChoices = valueAsArray.map((value) => {
        const choice = parsedChoices.find((ch) => ch.value === value);
        return [value, choice ? (0, exports.checkChoiceConditional)(choice.conditional, interview, path, user) : [false, undefined]];
    });
    if (valueAndChoices.find((valueAndChoice) => valueAndChoice[1][0] !== true) === undefined) {
        // All selected choices are still visible
        return [true, value];
    }
    // Some of the selected choices are not visible anymore, remove from value
    // and replace with corresponding value if necessary
    const modifiedValue = [];
    for (let i = 0; i < valueAndChoices.length; i++) {
        const [currentValue, [condResult, newValue]] = valueAndChoices[i];
        if (condResult) {
            modifiedValue.push(currentValue);
        }
        else if (!(0, LodashExtensions_1._isBlank)(newValue) && !modifiedValue.includes(newValue)) {
            modifiedValue.push(newValue);
        }
    }
    return [false, Array.isArray(value) ? modifiedValue : modifiedValue[0]];
};
exports.checkChoicesConditional = checkChoicesConditional;
//# sourceMappingURL=Conditional.js.map